import { StringValue } from "@bufbuild/protobuf";

import React, { useEffect, useState } from "react";

import { Button } from "@mui/material";
import { NIL } from "uuid";

import { AdminCreateDynamicGroupRequest } from "@safetyculture/s12-apis-connect-web/s12/groups/admin/v1/admin_groups_pb";
import {
  Rule,
  Rule_Combinator,
  Rule_Condition,
  Rule_Condition_Value,
  Rule_Domain,
  Rule_EntityType,
} from "@safetyculture/s12-apis-connect-web/s12/rules/v1/common_pb";

import { supportServiceConnectClient } from "../../../../api";
import GenericDialog from "../../../shared/generic-dialog";
import { FileUpload } from "./file-upload";
import { CreateResponse } from "./types";
import { Uploader } from "./uploader";
import { useDownloadExample } from "./use-download-example";
import { combinatorStringToEnum, operatorStringToEnum } from "./utils";

type BulkCreateDynamicGroupsModalProps = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  orgID: string;
};

export const BulkCreateDynamicGroupsModal = (
  props: BulkCreateDynamicGroupsModalProps,
) => {
  const { open, setOpen, orgID } = props;
  const [file, setFile] = useState<File | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [normalisedData, setNormalisedData] = useState<
    Record<string, AdminCreateDynamicGroupRequest>
  >({});
  const { downloadExample } = useDownloadExample();
  const [responses, setResponses] = useState<Record<string, CreateResponse>>(
    {},
  );

  const handleUpload = (file: File) => {
    setFile(file);
  };

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const csvString = reader.result as string;
        const lines = csvString.split("\n");
        const headers = lines[0]
          .split(",")
          .map((header) => header.trim().replace(/^"|"$/g, ""));

        const data = lines
          .slice(1)
          .map((line) =>
            line.split(",").map((cell) => cell.trim().replace(/^"|"$/g, "")),
          );

        if (data.some((row) => row.length !== headers.length)) {
          setErrorMessage("CSV format is incorrect. Please check the file.");
          return;
        }

        const normalisedData: AdminCreateDynamicGroupRequest[] = data.map(
          (row) => {
            const normalisedRow = new AdminCreateDynamicGroupRequest({
              organisationId: orgID,
              creator: NIL,
            });
            const rule = new Rule({
              conditions: [],
              combinator: Rule_Combinator.UNSPECIFIED, // Default value
              domain: Rule_Domain.GROUPS,
              orgId: orgID,
            });
            headers.forEach((header, index) => {
              switch (header) {
                case "name":
                  normalisedRow.name = row[index];
                  break;
                case "description":
                  normalisedRow.description = row[index];
                  break;
                case "combinator":
                  rule.combinator = combinatorStringToEnum(row[index]);
                  break;
                case "field_id_1":
                case "field_id_2":
                case "field_id_3":
                case "field_id_4":
                case "field_id_5":
                  if (!row[index]) {
                    break;
                  }
                  const values = row[index + 1]
                    .split("|")
                    .map((value) => value.trim());
                  const operator = operatorStringToEnum(row[index + 2]);
                  rule.conditions.push(
                    new Rule_Condition({
                      entityId: row[index],
                      entityType: Rule_EntityType.USER_FIELD,
                      operator,
                      values: values.map(
                        (value) =>
                          new Rule_Condition_Value({
                            value: {
                              case: "stringValue",
                              value: new StringValue({ value }),
                            },
                          }),
                      ),
                    }),
                  );
                  break;
                default:
                  // Ignore any other headers
                  break;
              }
            });
            normalisedRow.rule = rule;
            return normalisedRow;
          },
        );

        if (normalisedData.length === 0) {
          setErrorMessage("No valid data found in the CSV file.");
          return;
        }

        if (
          normalisedData.some(
            (row) =>
              !row.name ||
              row.rule?.conditions.length === 0 ||
              !row.rule?.combinator,
          )
        ) {
          setErrorMessage(
            "Each row must have a name, combinator, and at least one condition.",
          );
          return;
        }

        setErrorMessage("");

        // Convert to a record with group names as keys
        const normalisedDataRecord: Record<
          string,
          AdminCreateDynamicGroupRequest
        > = {};
        normalisedData.forEach((row) => {
          if (normalisedDataRecord[row.name]) {
            setErrorMessage(`Duplicate group name found: ${row.name}`);
            return;
          }
          normalisedDataRecord[row.name] = row;
        });

        setNormalisedData(normalisedDataRecord);
      };
      reader.readAsText(file);
    }
  }, [file, orgID]);

  const handleAckBtnClick = async () => {
    setIsUploading(true);
    const requests = Object.values(normalisedData).map((row) => {
      return row;
    });
    const res = supportServiceConnectClient.bulkCreateDynamicGroups({
      requests,
    });
    try {
      for await (const groupRes of res) {
        setResponses((prevResponses) => {
          if (!groupRes.group) {
            return prevResponses;
          }
          return {
            ...prevResponses,
            [groupRes.group.name]: {
              success: true,
              errorMessage: "",
            },
          };
        });
      }
    } catch (streamErr: unknown) {
      if (streamErr instanceof Error) {
        setErrorMessage("Stream failed: " + streamErr.message);
      } else {
        setErrorMessage("Stream failed: Unknown error");
      }
      setIsUploading(false);
    }
  };

  return (
    <GenericDialog
      title="Rule-based groups CSV upload"
      width="md"
      acknowledgeBtnLabel="Upload"
      open={open}
      disabled={file === null || isUploading}
      handleAckBtnClick={handleAckBtnClick}
      handleDialogClose={() => {
        setFile(undefined);
        setIsUploading(false);
        setOpen(false);
      }}
      hasAckBtn
      hasXCloseBtn
      data-testid="bulk-createdynamic-groups-modal"
    >
      <div>
        <Button onClick={downloadExample} variant="contained" color="secondary">
          Download Example CSV
        </Button>
        <p>
          Upload a CSV file to create rule-based groups in bulk. The CSV should
          contain the necessary fields for rule-based group creation. Drop the
          file below.
        </p>
        {isUploading && (
          <Uploader responses={responses} requests={normalisedData} />
        )}
        {!isUploading && <FileUpload onFileUpload={handleUpload} file={file} />}
        {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
      </div>
    </GenericDialog>
  );
};
